const SYNC_DB_NAME = "portfolio_offline"
const SYNC_STORE_NAME = "sync_queue"

export interface SyncRequest {
  id: string
  url: string
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
  headers?: Record<string, string>
  body?: unknown
  timestamp: number
  retries: number
  maxRetries: number
}

const openDB = async (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SYNC_DB_NAME, 1)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(SYNC_STORE_NAME)) {
        db.createObjectStore(SYNC_STORE_NAME, { keyPath: "id" })
      }
    }
  })
}

export const queueSyncRequest = async (request: Omit<SyncRequest, "id" | "timestamp" | "retries">) => {
  try {
    const db = await openDB()
    const tx = db.transaction(SYNC_STORE_NAME, "readwrite")
    const store = tx.objectStore(SYNC_STORE_NAME)

    const syncRequest: SyncRequest = {
      ...request,
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retries: 0,
    }

    store.add(syncRequest)

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(syncRequest)
      tx.onerror = () => reject(tx.error)
    })
  } catch (error) {
    console.error("Failed to queue sync request:", error)
    throw error
  }
}

export const getSyncQueue = async (): Promise<SyncRequest[]> => {
  try {
    const db = await openDB()
    const tx = db.transaction(SYNC_STORE_NAME, "readonly")
    const store = tx.objectStore(SYNC_STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  } catch (error) {
    console.error("Failed to get sync queue:", error)
    return []
  }
}

export const removeSyncRequest = async (id: string) => {
  try {
    const db = await openDB()
    const tx = db.transaction(SYNC_STORE_NAME, "readwrite")
    const store = tx.objectStore(SYNC_STORE_NAME)

    store.delete(id)

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(undefined)
      tx.onerror = () => reject(tx.error)
    })
  } catch (error) {
    console.error("Failed to remove sync request:", error)
  }
}

export const updateSyncRequestRetries = async (id: string, retries: number) => {
  try {
    const db = await openDB()
    const tx = db.transaction(SYNC_STORE_NAME, "readwrite")
    const store = tx.objectStore(SYNC_STORE_NAME)

    const getRequest = store.get(id)
    getRequest.onsuccess = () => {
      const item = getRequest.result
      if (item) {
        item.retries = retries
        store.put(item)
      }
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(undefined)
      tx.onerror = () => reject(tx.error)
    })
  } catch (error) {
    console.error("Failed to update sync request retries:", error)
  }
}

export const processSyncQueue = async () => {
  if (!navigator.onLine) return

  try {
    const queue = await getSyncQueue()

    for (const request of queue) {
      if (request.retries >= request.maxRetries) {
        await removeSyncRequest(request.id)
        continue
      }

      try {
        const response = await fetch(request.url, {
          method: request.method,
          headers: {
            "Content-Type": "application/json",
            ...request.headers,
          },
          body: request.body ? JSON.stringify(request.body) : undefined,
        })

        if (response.ok) {
          await removeSyncRequest(request.id)
          console.info(`Synced: ${request.method} ${request.url}`)
        } else {
          await updateSyncRequestRetries(request.id, request.retries + 1)
        }
      } catch (error) {
        await updateSyncRequestRetries(request.id, request.retries + 1)
        console.error(`Failed to sync ${request.url}:`, error)
      }
    }
  } catch (error) {
    console.error("Error processing sync queue:", error)
  }
}

export const setupSyncListener = () => {
  if (!navigator.onLine) {
    window.addEventListener("online", () => {
      processSyncQueue()
    })
  }
}
